import React from 'react';
import moment from 'moment';
import { Segment, Table, Button, Header, Form, Label, Message, Checkbox } from 'semantic-ui-react';
import { post } from '../../util/api';

// Giris kilitleri (server lib/loginGuard.js): son pencere icinde hatali giris denemesi olan e-posta ve IP'ler.
// Sinira ulasan "kilitli" gorunur; "Kilidi kaldir" pencere icindeki login-failed kayitlarini siler.
const REASONS = {
	bad_password: 'Yanlış şifre',
	user_not_found: 'Kayıtlı olmayan e-posta',
	invalid_email: 'Geçersiz e-posta',
	'2fa_bad_code': 'Hatalı 2FA kodu',
};
const reasonText = (r) =>
	String(r || '')
		.split(',')
		.map((it) => REASONS[it.trim()] || it.trim())
		.filter(Boolean)
		.join(', ');

export default function LoginLocks() {
	const [data, setData] = React.useState(null);
	const [loading, setLoading] = React.useState(false);
	const [busy, setBusy] = React.useState('');
	const [form, setForm] = React.useState({ email: '', ip: '' });
	const [notice, setNotice] = React.useState(null);
	// kalici IP engelleri (server lib/ipBlock.js, tablo login_blocked_ip)
	const [blocked, setBlocked] = React.useState(null);
	const [blockForm, setBlockForm] = React.useState({ pattern: '', note: '' });
	// kisa surede cok hesaba giris yapan IP'ler (takip; db/multiAccountIp.js)
	const [multi, setMulti] = React.useState(null);

	const load = React.useCallback(async () => {
		setLoading(true);
		try {
			const resp = await post('/live/admin/loginLocks', {});
			const body = (resp && resp.data) || {};
			if (body.success) setData(body);
			else setNotice({ error: true, text: body.message || 'Liste alınamadı.' });
		} catch (e) {
			setNotice({ error: true, text: 'Liste alınamadı.' });
		}
		setLoading(false);
	}, []);

	const loadBlocked = React.useCallback(async () => {
		try {
			const resp = await post('/live/admin/blockedIps', {});
			const body = (resp && resp.data) || {};
			if (body.success) setBlocked(body);
			else setNotice({ error: true, text: body.message || 'Engelli IP listesi alınamadı.' });
		} catch (e) {
			setNotice({ error: true, text: 'Engelli IP listesi alınamadı.' });
		}
	}, []);

	const loadMulti = React.useCallback(async () => {
		try {
			const resp = await post('/live/admin/multiAccountIps', { hours: 24, minAccounts: 5 });
			const body = (resp && resp.data) || {};
			if (body.success) setMulti(body);
		} catch (e) {
			// liste opsiyonel
		}
	}, []);

	React.useEffect(() => {
		load();
		loadBlocked();
		loadMulti();
	}, [load, loadBlocked, loadMulti]);

	const addBlock = async (preset) => {
		const pattern = ((preset && preset.pattern) || blockForm.pattern).trim();
		const note = ((preset && preset.note) || blockForm.note).trim();
		if (!pattern) return;
		if (!window.confirm(`${pattern} engellensin mi? Bu IP'den giriş ve oturumlu istekler reddedilir.`)) return;
		setBusy('block:' + pattern);
		try {
			const resp = await post('/live/admin/blockIp', { pattern, note });
			const body = (resp && resp.data) || {};
			if (body.success) {
				setNotice({ success: true, text: `${pattern} engellendi.` });
				setBlockForm({ pattern: '', note: '' });
			} else setNotice({ error: true, text: body.message || 'Engellenemedi.' });
		} catch (e) {
			setNotice({ error: true, text: 'Engellenemedi.' });
		}
		setBusy('');
		loadBlocked();
		loadMulti();
	};

	// "Kontrol edildi": sunucu o anki hesap/firma listesini saklar; sonradan yeni firmaya giris olursa isaret duser ve uyari yine gider
	const setReviewed = async (row, reviewed) => {
		setBusy('review:' + row.ip);
		try {
			const resp = await post('/live/admin/multiAccountIpReview', { ip: row.ip, reviewed, hours: (multi && multi.hours) || 24 });
			const body = (resp && resp.data) || {};
			if (!body.success) setNotice({ error: true, text: body.message || 'Kaydedilemedi.' });
		} catch (e) {
			setNotice({ error: true, text: 'Kaydedilemedi.' });
		}
		setBusy('');
		loadMulti();
	};

	const reviewNote = (rv) => {
		if (!rv) return null;
		let when = rv.reviewed_at ? moment(rv.reviewed_at).format('DD.MM HH:mm') : '';
		let base = `${when}${rv.reviewed_by ? ' · ' + rv.reviewed_by : ''} · ${rv.reviewed_accounts} hesap / ${rv.reviewed_firms} firma`;
		if (!rv.valid) return { text: `Kontrolden sonra +${rv.new_firms} yeni firma (${base})`, color: 'red' };
		if (rv.new_accounts) return { text: `+${rv.new_accounts} hesap, aynı firmalar (${base})`, color: 'grey' };
		return { text: base, color: 'grey' };
	};

	const removeBlock = async (row) => {
		if (!window.confirm(`${row.pattern} engeli kaldırılsın mı?`)) return;
		setBusy('unblock:' + row.id);
		try {
			const resp = await post('/live/admin/unblockIp', { id: row.id });
			const body = (resp && resp.data) || {};
			if (body.success) setNotice({ success: true, text: `${row.pattern} engeli kaldırıldı.` });
			else setNotice({ error: true, text: body.message || 'Engel kaldırılamadı.' });
		} catch (e) {
			setNotice({ error: true, text: 'Engel kaldırılamadı.' });
		}
		setBusy('');
		loadBlocked();
	};

	const unlock = async (params) => {
		const label = params.email || params.ip;
		if (!window.confirm(`${label} için giriş kilidi kaldırılsın mı?`)) return;
		setBusy(label);
		try {
			const resp = await post('/live/admin/loginUnlock', params);
			const body = (resp && resp.data) || {};
			if (body.success) setNotice({ success: true, text: `${label}: kilit kaldırıldı (${body.deleted || 0} kayıt silindi).` });
			else setNotice({ error: true, text: body.message || 'Kilit kaldırılamadı.' });
		} catch (e) {
			setNotice({ error: true, text: 'Kilit kaldırılamadı.' });
		}
		setBusy('');
		load();
	};

	const renderTable = (rows, field, max) => (
		<Table compact celled size="small">
			<Table.Header>
				<Table.Row>
					<Table.HeaderCell>{field === 'email' ? 'E-posta' : 'IP'}</Table.HeaderCell>
					<Table.HeaderCell>Hatalı deneme</Table.HeaderCell>
					<Table.HeaderCell>Durum</Table.HeaderCell>
					<Table.HeaderCell>Son deneme</Table.HeaderCell>
					<Table.HeaderCell>{field === 'email' ? 'Farklı IP' : 'Farklı e-posta'}</Table.HeaderCell>
					<Table.HeaderCell>Sebep</Table.HeaderCell>
					<Table.HeaderCell />
				</Table.Row>
			</Table.Header>
			<Table.Body>
				{!rows || !rows.length ? (
					<Table.Row>
						<Table.Cell colSpan="7">Kayıt yok.</Table.Cell>
					</Table.Row>
				) : (
					rows.map((r) => (
						<Table.Row key={r.key} negative={r.locked}>
							<Table.Cell>{r.key}</Table.Cell>
							<Table.Cell>
								{r.fails} / {max}
							</Table.Cell>
							<Table.Cell>{r.locked ? <Label color="red" size="small" content="Kilitli" /> : <Label size="small" content="Açık" />}</Table.Cell>
							<Table.Cell>{r.last_at ? moment(r.last_at).format('DD.MM.YYYY HH:mm:ss') : ''}</Table.Cell>
							<Table.Cell>{r.other_count}</Table.Cell>
							<Table.Cell>{reasonText(r.reasons)}</Table.Cell>
							<Table.Cell textAlign="right">
								<Button size="mini" loading={busy === r.key} onClick={() => unlock(field === 'email' ? { email: r.key } : { ip: r.key })}>
									Kilidi kaldır
								</Button>
							</Table.Cell>
						</Table.Row>
					))
				)}
			</Table.Body>
		</Table>
	);

	return (
		<Segment basic loading={loading && !data}>
			<Header as="h3">
				Giriş Kilitleri
				<Header.Subheader>
					{data
						? `Son ${data.windowMin} dakikadaki hatalı girişler. E-posta başına ${data.maxPerEmail}, IP başına ${data.maxPerIp} denemeden sonra giriş ${data.windowMin} dakika kapanır.`
						: 'Yükleniyor...'}
				</Header.Subheader>
			</Header>
			{notice ? (
				<Message size="small" positive={!!notice.success} negative={!!notice.error} onDismiss={() => setNotice(null)} content={notice.text} />
			) : null}
			<Form
				onSubmit={() => {
					const params = {};
					if (form.email.trim()) params.email = form.email.trim();
					if (form.ip.trim()) params.ip = form.ip.trim();
					if (params.email || params.ip) unlock(params);
				}}
			>
				<Form.Group widths="equal">
					<Form.Input label="E-posta" placeholder="ornek@firma.com" value={form.email} onChange={(e, { value }) => setForm({ ...form, email: value })} />
					<Form.Input label="IP" placeholder="1.2.3.4" value={form.ip} onChange={(e, { value }) => setForm({ ...form, ip: value })} />
				</Form.Group>
				<Button type="submit" primary disabled={!form.email.trim() && !form.ip.trim()}>
					Kilidi kaldır
				</Button>
				<Button type="button" basic onClick={load} loading={loading}>
					Yenile
				</Button>
			</Form>
			<Header as="h4">E-postalar</Header>
			{renderTable(data && data.emails, 'email', data && data.maxPerEmail)}
			<Header as="h4">IP'ler</Header>
			{renderTable(data && data.ips, 'ip', data && data.maxPerIp)}

			<Header as="h3" style={{ marginTop: '2em' }}>
				Çok hesaba giren IP'ler
				<Header.Subheader>
					Son {(multi && multi.hours) || 24} saatte en az {(multi && multi.minAccounts) || 5} farklı hesaba başarılı giriş yapan IP'ler. Engel değil, takip
					listesi: şirket VPN'i ya da çağrı merkezi de çıkabilir. Firma sayısı düşükse genelde aynı firmanın ekibidir. Kısa sürede çok firmaya giriş
					olursa WhatsApp grubuna da uyarı gider. "Kontrol edildi" işaretlenen IP o anki hesap ve firmalarıyla kaydedilir; sonradan yeni bir firmaya
					girerse işaret kalkar, satır kırmızı olur ve uyarı yine gider.
				</Header.Subheader>
			</Header>
			<Table compact celled size="small">
				<Table.Header>
					<Table.Row>
						<Table.HeaderCell>IP</Table.HeaderCell>
						<Table.HeaderCell>Hesap</Table.HeaderCell>
						<Table.HeaderCell>Firma</Table.HeaderCell>
						<Table.HeaderCell>İlk / son giriş</Table.HeaderCell>
						<Table.HeaderCell>Hesaplar</Table.HeaderCell>
						<Table.HeaderCell />
						<Table.HeaderCell>Kontrol edildi</Table.HeaderCell>
					</Table.Row>
				</Table.Header>
				<Table.Body>
					{!multi || !multi.rows || !multi.rows.length ? (
						<Table.Row>
							<Table.Cell colSpan="7">{multi ? 'Kayıt yok.' : 'Yükleniyor...'}</Table.Cell>
						</Table.Row>
					) : (
						multi.rows.map((r) => (
							<Table.Row
								key={r.ip}
								warning={!r.blocked && r.firms >= 3 && !(r.review && r.review.valid)}
								negative={!r.blocked && !!r.review && !r.review.valid}
								disabled={r.blocked}
							>
								<Table.Cell>
									<a href={`https://www.abuseipdb.com/check/${r.ip}`} target="_blank" rel="noopener noreferrer">
										{r.ip}
									</a>
								</Table.Cell>
								<Table.Cell>{r.accounts}</Table.Cell>
								<Table.Cell>{r.firms}</Table.Cell>
								<Table.Cell>
									{r.first_at ? moment(r.first_at).format('DD.MM HH:mm') : ''} – {r.last_at ? moment(r.last_at).format('DD.MM HH:mm') : ''}
								</Table.Cell>
								<Table.Cell style={{ maxWidth: 420, whiteSpace: 'normal', fontSize: '0.9em' }}>
									{(r.emails || []).join(', ')}
									{r.accounts > (r.emails || []).length ? ` (+${r.accounts - (r.emails || []).length})` : ''}
								</Table.Cell>
								<Table.Cell textAlign="right">
									{r.blocked ? (
										<Label size="small" color="red" content="Engelli" />
									) : (
										<Button
											size="mini"
											basic
											color="red"
											loading={busy === 'block:' + r.ip}
											onClick={() => addBlock({ pattern: r.ip, note: `Çok hesaplı giriş: ${r.accounts} hesap, ${r.firms} firma` })}
										>
											Engelle
										</Button>
									)}
								</Table.Cell>
								<Table.Cell style={{ minWidth: 150 }}>
									<Checkbox
										checked={!!(r.review && r.review.valid)}
										disabled={r.blocked || busy === 'review:' + r.ip}
										onChange={(e, { checked }) => setReviewed(r, checked)}
									/>
									{reviewNote(r.review) ? (
										<div style={{ fontSize: '0.8em', marginTop: 4, color: reviewNote(r.review).color === 'red' ? '#db2828' : '#767676' }}>
											{reviewNote(r.review).text}
										</div>
									) : null}
								</Table.Cell>
							</Table.Row>
						))
					)}
				</Table.Body>
			</Table>

			<Header as="h3" style={{ marginTop: '2em' }}>
				Engelli IP'ler
				<Header.Subheader>
					Bu IP'lerden giriş ve oturumlu istekler kalıcı olarak reddedilir (403). Sonu nokta ile biten değer bütün bloğu engeller, örneğin
					149.102.229.
				</Header.Subheader>
			</Header>
			<Form onSubmit={() => addBlock()}>
				<Form.Group widths="equal">
					<Form.Input
						label="IP veya blok"
						placeholder="1.2.3.4 veya 1.2.3."
						value={blockForm.pattern}
						onChange={(e, { value }) => setBlockForm({ ...blockForm, pattern: value })}
					/>
					<Form.Input label="Not" placeholder="Neden engellendi" value={blockForm.note} onChange={(e, { value }) => setBlockForm({ ...blockForm, note: value })} />
				</Form.Group>
				<Button type="submit" color="red" disabled={!blockForm.pattern.trim()} loading={busy === 'block:' + blockForm.pattern.trim()}>
					Engelle
				</Button>
			</Form>
			<Table compact celled size="small">
				<Table.Header>
					<Table.Row>
						<Table.HeaderCell>IP / blok</Table.HeaderCell>
						<Table.HeaderCell>Not</Table.HeaderCell>
						<Table.HeaderCell>Ekleyen</Table.HeaderCell>
						<Table.HeaderCell>Tarih</Table.HeaderCell>
						<Table.HeaderCell />
					</Table.Row>
				</Table.Header>
				<Table.Body>
					{!blocked || ((!blocked.rows || !blocked.rows.length) && (!blocked.env || !blocked.env.length)) ? (
						<Table.Row>
							<Table.Cell colSpan="5">{blocked ? 'Engelli IP yok.' : 'Yükleniyor...'}</Table.Cell>
						</Table.Row>
					) : (
						<>
							{(blocked.rows || []).map((row) => (
								<Table.Row key={row.id}>
									<Table.Cell>{row.pattern}</Table.Cell>
									<Table.Cell>{row.note}</Table.Cell>
									<Table.Cell>{row.created_by}</Table.Cell>
									<Table.Cell>{row.created_at ? moment(row.created_at).format('DD.MM.YYYY HH:mm') : ''}</Table.Cell>
									<Table.Cell textAlign="right">
										<Button size="mini" loading={busy === 'unblock:' + row.id} onClick={() => removeBlock(row)}>
											Engeli kaldır
										</Button>
									</Table.Cell>
								</Table.Row>
							))}
							{(blocked.env || []).map((pattern) => (
								<Table.Row key={'env:' + pattern}>
									<Table.Cell>{pattern}</Table.Cell>
									<Table.Cell colSpan="3">Sunucu env'inde (LOGIN_BLOCKED_IPS) tanımlı, buradan kaldırılamaz</Table.Cell>
									<Table.Cell />
								</Table.Row>
							))}
						</>
					)}
				</Table.Body>
			</Table>
		</Segment>
	);
}
